// Pure decision logic for the "Agent Empty" retry circuit-breaker.
//
// "Agent Empty" is a per-node / per-endpoint onboarding state: a freshly added
// node returns HTTP 400 "Agent info not found." on each /agent_info request
// until its agent first checks in. It is NOT a global server-reachability
// signal, so the retry budget + breaker must be tracked PER request, not in a
// single shared counter. (A shared counter is drained in one concurrent batch
// when a page fans out several /agent_info requests at once.)
//
// This module holds NO state and performs NO side effects (no timers, no
// logging). The caller owns the per-key state map and the actual retry/timer,
// which keeps this decision purely unit-testable.

export const AGENT_EMPTY_DEFAULTS = {
  maxRetries: 5,
  cooldownMs: 60000,
};

const EMPTY_STATE = { retryCount: 0, breakerExpiresAt: 0 };

// Given a request key's current state and the current time, decide what to do
// with one more "Agent Empty" failure. Returns the next state plus an action:
//   "retry"    -> schedule the retry after the configured delay
//   "cooldown" -> breaker just tripped; do nothing (caller may log)
//   "wait"     -> inside an active cooldown; do nothing
export function decideAgentEmpty(state, now, opts) {
  const { maxRetries, cooldownMs } = { ...AGENT_EMPTY_DEFAULTS, ...opts };
  const current = state || EMPTY_STATE;

  if (now < current.breakerExpiresAt) {
    return { action: "wait", state: { ...current } };
  }
  if (current.retryCount >= maxRetries) {
    return {
      action: "cooldown",
      state: { retryCount: 0, breakerExpiresAt: now + cooldownMs },
    };
  }
  return {
    action: "retry",
    state: { retryCount: current.retryCount + 1, breakerExpiresAt: 0 },
  };
}
